import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  CacheService,
  SearchExpression,
  SortExpression,
  IQueryResult,
  SKIP_ERROR_HEADER_KEY,
  IEntityService,
  Key,
  AuthService,
  NotFoundEntity,
  UpdateEntity,
} from '@kpd/framework-ngx';
import { User } from 'app/shared/models/user';
import { UserManagementQueryService } from 'app/shared/services/usermanagement.query.service';
import { map, catchError, tap } from 'rxjs/operators';
import { apiUrl } from 'environments/environment';
import { PERMISSIONS, Permissions } from './permissions';
import * as saveAs from 'file-saver';

export interface IUserOverviewDTO {
  upn: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface IUserOverviewRootDTO {
  totalCount: number;
  result: IUserOverviewDTO[];
}

export interface IUserDTO {
  upn: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  culture?: string;
  isEnabled?: boolean;
  tenants?: any;
  displayName: string;
  allowedAuthenticationProviders?: string;
  lastLoginDate?: Date;
  isSystemUser: boolean;
}

@Injectable()
export class UserService extends UserManagementQueryService implements IEntityService {
  constructor(httpClient: HttpClient, cache: CacheService, private authService: AuthService) {
    super(httpClient, cache, '_user');
  }

  get hasSystemUserManagePermission(): boolean {
    const id = this.authService.getIdentity();
    const systemUserManagePermissionGuid = PERMISSIONS.find((p) => p.name === Permissions.SystemUserManage)!.id;
    return !!systemUserManagePermissionGuid && id.permissions.includes(systemUserManagePermissionGuid);
  }

  getById(key: Key<string>, clearCache: boolean = false): Observable<User> {
    return this.getDtoById(
      `tenants/${this.authService.getTenant().key.toString()}/users/${encodeURIComponent(key.toString())}`,
      key.toString(),
      clearCache,
      (dto: any) => {
        return new User(dto);
      },
    ).pipe(
      catchError(() => {
        return of(new NotFoundEntity() as any);
      }),
    );
  }

  save(user: User | UpdateEntity<User>): Observable<User> {
    const payload: User = user instanceof User ? user : user.dto;
    if (!payload.isNew()) {
      this.clearCache(payload.key.toString());
      // Update
      const putUrl = `${apiUrl()}/users/${encodeURIComponent(payload.key.toString())}`;
      return this.httpClient.put<any>(putUrl, payload).pipe(
        map((dto) => {
          // manually merge the tenants, because they aren't returned witht he PUT.
          const newUser = new User(dto);
          newUser.tenants = payload.tenants;
          return newUser;
        }),
      );
    }
    // New
    const postUrl = `${apiUrl()}/users`;
    return this.httpClient
      .post<any>(postUrl, payload, { headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['409', '400']) })
      .pipe(map((dto) => new User(dto)));
  }

  get(
    searchExpression?: SearchExpression,
    sortExpression?: SortExpression,
    skip?: number,
    take?: number,
  ): Observable<IQueryResult> {
    return super
      .getEntities(
        `tenants/${this.authService.getTenant().key.toString()}/users`,
        searchExpression,
        sortExpression,
        skip,
        take,
      )
      .pipe(
        map((queryResult) => {
          return {
            result: queryResult.result
              .filter((item: IUserDTO) => this.hasSystemUserManagePermission || !item.isSystemUser)
              .map((item) => new User(item)),
            totalCount: queryResult.totalCount,
          };
        }),
      );
  }

  delete(_id: any): Observable<any> {
    return this.httpClient.delete<any>(
      `${apiUrl()}/tenants/${this.authService.getTenant().key.toString()}/users/${encodeURIComponent(_id)}`,
      {
        params: { forceDelete: 'true' },
      },
    );
  }

  toggleRole(userKey: Key<string>, applicationName: string, role: string, add: boolean): Observable<any> {
    this.clearCache(userKey.toString());

    const url = `${apiUrl()}/tenants/${this.authService.getTenant().key.toString()}/application/${encodeURIComponent(
      applicationName,
    )}/roles/${encodeURIComponent(role)}/users/${encodeURIComponent(userKey.toString())}`;
    return add
      ? this.httpClient.post<any>(url, {}, { headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['409', '404']) })
      : this.httpClient.delete<any>(url, {
          headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['409', '404']),
        });
  }

  changePassword(
    userKey: Key<string>,
    oldPassword: string,
    newPassword: string,
    localUser: boolean,
  ): Observable<boolean> {
    if (localUser) {
      return this.httpClient
        .post<void>(`${apiUrl()}/users/${encodeURIComponent(userKey.toString())}/password/reset`, {
          newPassword,
        })
        .pipe(map(() => true));
    }

    return this.httpClient
      .put<void>(
        `${apiUrl()}/users/${encodeURIComponent(userKey.toString())}/password`,
        { oldPassword, newPassword },
        { headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['400']) },
      )
      .pipe(map(() => true));
  }

  addUserToTenant(userKey: Key<string>): Observable<User> {
    return this.httpClient
      .post<IUserDTO>(
        `${apiUrl()}/tenants/${this.authService.getTenant().key.toString()}/users/${encodeURIComponent(
          userKey.toString(),
        )}`,
        {},
      )
      .pipe(
        map((dto: IUserDTO) => {
          return new User(dto);
        }),
      );
  }

  unlockUser(upn: string): Observable<any> {
    return this.httpClient.post<void>(`${apiUrl()}/users/${upn}/unlock`, {});
  }

  exportUsers(): Observable<any> {
    const tenantId = this.authService.getTenant().key.toString();
    return this.httpClient
      .get(`${apiUrl()}/tenants/${tenantId}/Users/exportwithlicenses`, {
        responseType: 'arraybuffer',
        observe: 'response',
      })
      .pipe(
        tap((response) => {
          if (response.body) {
            const blob = new Blob([response.body]);
            const header = response.headers.get('content-disposition') as string;
            const start = header.indexOf('filename=') + 9;
            const end = header.indexOf(';', start);
            const fileName = header.substring(start, end);
            saveAs(blob, fileName || '');
          }
        }),
      );
  }

  importUsers(importFile: FormData): Observable<{ result: Record<string, { isSuccess: boolean; message: string }> }> {
    const tenantId = this.authService.getTenant().key.toString();
    return this.httpClient.post<{ result: Record<string, { isSuccess: boolean; message: string }> }>(
      `${apiUrl()}/tenants/${tenantId}/users/import`,
      importFile,
      { headers: new HttpHeaders().set(SKIP_ERROR_HEADER_KEY, ['409']) },
    );
  }
}
